import type { Weapon } from "../weapons/weapon";

// Live sight readouts computed by the engine each frame.
export type SightTelemetry = {
  rangeM: number | null; // ground range under the crosshair; null when aiming at sky
  altitudeM: number;
  tofS: number | null; // projectile time of flight to the aim point; null for hitscan
  zoomFactor: number;
};

const METERS_TO_FEET = 3.281;

function formatRange(rangeM: number | null): string {
  if (rangeM === null) return "----M";
  return `${Math.round(rangeM).toString().padStart(4, "0")}M`;
}

function formatAltitude(altitudeM: number): string {
  return `${Math.round(altitudeM * METERS_TO_FEET).toString().padStart(5, "0")}FT`;
}

export class AmmoHud {
  private readonly el: HTMLElement;
  private lastHtml = "";

  constructor() {
    const el = document.querySelector("[data-slot='weapon']") as HTMLElement | null;
    if (!el) throw new Error("Missing HUD weapon slot");
    this.el = el;
  }

  update(weapons: readonly Weapon[], activeIdx: number, telemetry?: SightTelemetry, spotter = false): void {
    const activeWeapon = weapons[activeIdx];
    if (!activeWeapon) return;

    const hud = activeWeapon.getHudState();
    this.el.className = `hud-weapon-frame hud-weapon-frame--${spotter ? "spotter" : hud.weaponId}`;

    const selector = [
      ...weapons.map((weapon, idx) => {
        const item = weapon.getHudState();
        const active = !spotter && idx === activeIdx;
        return `
          <div class="hud-weapon-frame__selector-item${active ? " is-active" : ""}">
            <span class="hud-weapon-frame__selector-slot">[${idx + 1}]</span>
            <span class="hud-weapon-frame__selector-label">${weapon.label}</span>
            <span class="hud-weapon-frame__selector-state">${item.shortText}</span>
          </div>
        `;
      }),
      `
          <div class="hud-weapon-frame__selector-item${spotter ? " is-active" : ""}">
            <span class="hud-weapon-frame__selector-slot">[4]</span>
            <span class="hud-weapon-frame__selector-label">SPOTTER</span>
            <span class="hud-weapon-frame__selector-state">SAFE</span>
          </div>
        `,
    ].join("");

    const html = `
      <div class="hud-weapon-frame__box">
        <div class="hud-weapon-frame__corners"></div>
        <div class="hud-weapon-frame__top">
          <span class="hud-weapon-frame__eyebrow">${spotter ? "SPOTTER SCOPE" : activeWeapon.label}</span>
          <span class="hud-weapon-frame__mode">${spotter ? "OBSERVATION" : hud.modeLabel}</span>
        </div>
        ${spotter ? "" : `<div class="hud-weapon-frame__left">
          <span class="hud-weapon-frame__label">${hud.primaryLabel}</span>
          <span class="hud-weapon-frame__value">${hud.primaryText}</span>
          <div class="hud-weapon-frame__bar"><span style="height:${Math.round(hud.primaryValue * 100)}%"></span></div>
        </div>`}
        <div class="hud-weapon-frame__right">
          <div class="hud-weapon-frame__datum">
            <span class="hud-weapon-frame__datum-label">${hud.secondaryLabel}</span>
            <span class="hud-weapon-frame__datum-value">${hud.secondaryText}</span>
          </div>
          <div class="hud-weapon-frame__datum">
            <span class="hud-weapon-frame__datum-label">ZOOM</span>
            <span class="hud-weapon-frame__datum-value">${hud.zoomText}${telemetry && telemetry.zoomFactor > 1.05 ? ` ×${telemetry.zoomFactor.toFixed(1)}` : ""}</span>
          </div>
          <div class="hud-weapon-frame__datum">
            <span class="hud-weapon-frame__datum-label">STATUS</span>
            <span class="hud-weapon-frame__datum-value">${spotter ? "WEAPONS SAFE" : hud.statusText}</span>
          </div>
        </div>
        <div class="hud-weapon-frame__telemetry hud-weapon-frame__telemetry--left">
          <span class="hud-weapon-frame__telemetry-label">RNG</span>
          <span class="hud-weapon-frame__telemetry-value">${formatRange(telemetry?.rangeM ?? null)}</span>
          ${telemetry?.tofS != null ? `
          <span class="hud-weapon-frame__telemetry-label">TOF</span>
          <span class="hud-weapon-frame__telemetry-value">${telemetry.tofS.toFixed(1)}S</span>` : ""}
        </div>
        <div class="hud-weapon-frame__telemetry hud-weapon-frame__telemetry--right">
          <span class="hud-weapon-frame__telemetry-label">ALT</span>
          <span class="hud-weapon-frame__telemetry-value">${telemetry ? formatAltitude(telemetry.altitudeM) : "-----"}</span>
        </div>
        <div class="hud-weapon-frame__bottom">${selector}</div>
      </div>
    `;
    // Readouts are quantized (toFixed/rounded), so most frames produce
    // identical markup — skip the DOM write on those.
    if (html === this.lastHtml) return;
    this.lastHtml = html;
    this.el.innerHTML = html;
  }
}
